// ── 직원 급여 정보 탭 ────────────────────────────────────────────────────────
// 직원별 급여 프로필(기본급·4대보험 가입·부양가족·계좌) + 고정수당 금액 관리
// 모달은 PayrollEmployeeModal.jsx로 분리

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { todayKST } from '../../../utils/date';
import PayrollEmployeeModal from './PayrollEmployeeModal';
import { usePayrollProfileHistory } from './usePayrollProfileHistory';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnPrimary = { ...stdBlackBtn };
const btnOutline = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};

export default function PayrollEmployees({ companyCode }) {
  const { showToast } = useToast();

  const [profiles, setProfiles] = useState([]);
  const [payItems, setPayItems] = useState([]);  // 고정수당 항목 목록
  const [allUsers, setAllUsers] = useState([]);   // 직원 목록 (프로필 미등록 필터용)
  const [bankCodes, setBankCodes] = useState([]); // 은행코드 활성 목록 (자동완성용)
  const [loading, setLoading] = useState(true);
  // 퇴사자 보기 토글 — 기본 OFF (재직자 + 급여일 전까지의 직전 급여월 퇴사자만 서버가 반환)
  const [showResigned, setShowResigned] = useState(false);

  // 모달 상태
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null); // null = 신규
  const [editName, setEditName] = useState(''); // 수정 대상 직원 이름 (모달 제목 표시용)
  const [form, setForm] = useState({});
  const [itemValues, setItemValues] = useState([]); // [{ pay_item_id, item_name, amount }]
  const [saving, setSaving] = useState(false);
  const {
    profileHistory,
    setProfileHistory,
    fetchProfileHistory,
    buildItemPayload,
    buildProfileHistoryBody,
    loadHistoryToForm,
    deleteHistory,
  } = usePayrollProfileHistory({ payItems, setForm, setItemValues, showToast });

  // ── 데이터 로드 ────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [pRes, iRes, uRes, bcRes] = await Promise.all([
      apiClient.get(`/api/admin/payroll/employees?company_code=${companyCode}${showResigned ? '&include_resigned=1' : ''}`),
      apiClient.get(`/api/admin/payroll/pay-items?company_code=${companyCode}`),
      apiClient.get(`/api/admin/payroll/company-users?company_code=${companyCode}`),
      apiClient.get('/api/admin/payout/bank-codes'), // 은행코드 활성 목록 (전역)
    ]);
    if (pRes.ok) setProfiles(pRes.data?.data || []);
    // 지급항목(earning) 전체 — 기본급은 위 기본정보에서 관리하므로 제외
    if (iRes.ok) setPayItems((iRes.data?.data || []).filter((i) =>
      i.type === 'earning'
      && i.is_active
      && !['기본급', '미사용연차수당'].includes(i.name)
    ));
    if (uRes.ok) setAllUsers(uRes.data?.data || []);
    if (bcRes.data?.success) setBankCodes(bcRes.data.data || []);
    setLoading(false);
  }, [companyCode, showResigned]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 등록된 직원 ID Set (드롭다운에서 등록 가능/이미 등록됨 구분용)
  const registeredUserIds = new Set(profiles.map((p) => Number(p.user_id)));

  // ── 모달 열기 (신규) ───────────────────────────────────────────────────────
  const openNewModal = () => {
    setEditId(null);
    setEditName('');
    setForm({
      user_id: '', pay_type: 'monthly', base_salary: '',
      national_pension_yn: true, health_insurance_yn: true, employment_ins_yn: true,
      dependents_count: '1', bank_name: '', bank_account: '', memo: '',
      bank_effective_from: todayKST(), bank_change_mode: 'today', bank_change_reason: '',
      payroll_effective_from: todayKST(), payroll_change_reason: '',
      probation_months: '0', probation_pct: '90', probation_period_unit: 'month',
    });
    setItemValues(payItems.map((i) => ({ pay_item_id: i.id, item_name: i.name, calc_type: i.calc_type, amount: '', is_enabled: true })));
    setProfileHistory([]);
    setModalOpen(true);
  };

  // ── 모달 열기 (수정) ───────────────────────────────────────────────────────
  const openEditModal = async (profile) => {
    setEditId(profile.id);
    setEditName(profile.user_name || '');
    const [res] = await Promise.all([
      apiClient.get(`/api/admin/payroll/employees/${profile.id}`),
      fetchProfileHistory(profile.id),
    ]);
    if (!res.ok) { showToast('프로필 조회 실패', 'error'); return; }
    const d = res.data.data;
    setForm({
      user_id: d.user_id,
      pay_type: d.pay_type || 'monthly',
      base_salary: fmt(d.base_salary),
      national_pension_yn: d.national_pension_yn,
      health_insurance_yn: d.health_insurance_yn,
      employment_ins_yn: d.employment_ins_yn,
      dependents_count: String(d.dependents_count || 1),
      bank_name: d.bank_name || '',
      bank_account: d.bank_account || '',
      original_bank_name: d.bank_name || '',
      original_bank_account: d.bank_account || '',
      bank_effective_from: todayKST(),
      bank_change_mode: 'today',
      bank_change_reason: '',
      payroll_effective_from: todayKST(),
      payroll_change_reason: '',
      memo: d.memo || '',
      probation_months: String(d.probation_months || 0),
      probation_pct: String(Math.round((Number(d.probation_ratio) || 0.9) * 100)),
      probation_period_unit: d.probation_period_unit === 'day' ? 'day' : 'month',
    });
    // 기존 값 + 미등록 항목 보충 (is_enabled, calc_type 포함)
    const existMap = {};
    (d.item_values || []).forEach((v) => { existMap[v.pay_item_id] = v; });
    setItemValues(payItems.map((i) => ({
      pay_item_id: i.id,
      item_name: i.name,
      calc_type: i.calc_type,
      amount: existMap[i.id] ? fmt(existMap[i.id].amount) : '',
      is_enabled: existMap[i.id] ? existMap[i.id].is_enabled !== false : true,
    })));
    setModalOpen(true);
  };

  // ── 저장 ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editId && !form.user_id) { showToast('직원을 선택하세요.', 'info'); return; }
    setSaving(true);

    const body = {
      ...form,
      company_code: companyCode,
      base_salary: parse(form.base_salary),
      dependents_count: Number(form.dependents_count) || 1,
      probation_months: Number(form.probation_months) || 0,
      probation_ratio: (Number(form.probation_pct) || 90) / 100, // % → 소수 (90 → 0.9)
      probation_period_unit: form.probation_period_unit === 'day' ? 'day' : 'month',
    };
    delete body.probation_pct; // API에는 ratio만 전송

    delete body.original_bank_name;
    delete body.original_bank_account;
    const bankChanged = editId
      ? String(form.bank_name || '').trim() !== String(form.original_bank_name || '').trim()
        || String(form.bank_account || '').replace(/\D/g, '') !== String(form.original_bank_account || '').replace(/\D/g, '')
      : Boolean(form.bank_name || form.bank_account);
    if (!bankChanged) {
      delete body.bank_effective_from;
      delete body.bank_change_mode;
      delete body.bank_change_reason;
    }

    const effectiveFrom = body.payroll_effective_from || todayKST();
    const isFuturePayrollChange = editId && effectiveFrom > todayKST();
    const profileHistoryBody = buildProfileHistoryBody({ form, body, itemValues });
    delete body.payroll_effective_from;
    delete body.payroll_change_reason;

    if (isFuturePayrollChange && bankChanged) {
      setSaving(false);
      showToast('미래 급여정보 예약과 계좌 변경은 한 번에 저장할 수 없습니다. 계좌는 계좌 적용일로 먼저 저장해 주세요.', 'info');
      return;
    }

    let profileId = editId;
    if (isFuturePayrollChange) {
      const res = await apiClient.put(`/api/admin/payroll/employees/${editId}/profile-history`, profileHistoryBody);
      if (!res.ok) { setSaving(false); showToast(res.data?.message || '급여정보 예약 실패', 'error'); return; }
    } else if (editId) {
      const res = await apiClient.put(`/api/admin/payroll/employees/${editId}`, body);
      if (!res.ok) { setSaving(false); showToast(res.data?.message || '수정 실패', 'error'); return; }
    } else {
      const res = await apiClient.post('/api/admin/payroll/employees', body);
      if (!res.ok) { setSaving(false); showToast(res.data?.message || '등록 실패', 'error'); return; }
      profileId = res.data?.data?.id;
    }

    // 고정수당 저장
    if (profileId && itemValues.length > 0 && !isFuturePayrollChange) {
      await apiClient.put(`/api/admin/payroll/employees/${profileId}/item-values`, {
        items: buildItemPayload(itemValues),
      });
    }
    if (profileId && !isFuturePayrollChange) {
      const historyRes = await apiClient.put(`/api/admin/payroll/employees/${profileId}/profile-history`, profileHistoryBody);
      if (!historyRes.ok) {
        setSaving(false);
        showToast(historyRes.data?.message || '급여정보 이력 저장 실패', 'error');
        return;
      }
    }

    setSaving(false);
    showToast(isFuturePayrollChange ? '급여정보 변경을 예약했습니다.' : (editId ? '프로필이 수정되었습니다.' : '직원이 등록되었습니다.'), 'success');
    setModalOpen(false);
    fetchAll();
  };

  // ── 비활성화 ───────────────────────────────────────────────────────────────
  const handleDeactivate = async (id) => {
    if (!window.confirm('해당 직원의 급여 프로필을 비활성화하시겠습니까?')) return;
    const res = await apiClient.delete(`/api/admin/payroll/employees/${id}`);
    if (res.ok) { showToast('비활성화 완료', 'success'); fetchAll(); }
    else showToast(res.data?.message || '실패', 'error');
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 헤더 + 추가 버튼 */}
      <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-sub)' }}>
          직원 급여 프로필 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>({profiles.length}명)</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            style={showResigned
              ? { ...btnOutline, color: 'var(--std-pill-on-text, #1d7de6)', background: 'var(--std-pill-on-bg, rgba(59,154,242,0.12))', fontWeight: 700 }
              : btnOutline}
            onClick={() => setShowResigned((v) => !v)}
          >
            퇴사자 보기{showResigned ? ' ✓' : ''}
          </button>
          <button style={btnPrimary} onClick={openNewModal}>+ 직원 등록</button>
        </div>
      </div>

      {/* 프로필 목록 */}
      {profiles.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '40px 20px' }}>
          등록된 직원이 없습니다. "직원 등록" 버튼을 눌러 추가하세요.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {profiles.map((p) => (
            <div key={p.id} style={{
              ...cardStyle, padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: p.resign_date ? 'var(--text-muted)' : 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {p.user_name}
                  {/* 퇴사자 뱃지: resign_date가 있으면 퇴사일 표시 */}
                  {p.resign_date && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#fef2f2', color: '#dc2626' }}>
                      {p.resign_date.slice(0, 10)} 퇴사
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {p.pay_type === 'monthly' ? '월급' : '시급'} · 기본급 {fmt(p.base_salary)}원
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 6 }}>
                {p.national_pension_yn && <span style={{ background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>국민</span>}
                {p.health_insurance_yn && <span style={{ background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>건강</span>}
                {p.employment_ins_yn && <span style={{ background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>고용</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                부양가족 {p.dependents_count || 1}명
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...btnOutline, padding: '4px 10px', fontSize: 11 }} onClick={() => openEditModal(p)}>수정</button>
                <button style={{ ...btnOutline, padding: '4px 10px', fontSize: 11, color: '#dc2626' }} onClick={() => handleDeactivate(p.id)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 등록/수정 모달 */}
      {modalOpen && (
        <PayrollEmployeeModal
          editId={editId}
          editName={editName}
          form={form}
          setForm={setForm}
          itemValues={itemValues}
          setItemValues={setItemValues}
          allUsers={allUsers}
          registeredUserIds={registeredUserIds}
          bankCodes={bankCodes}
          profileHistory={profileHistory}
          onLoadHistory={loadHistoryToForm}
          onDeleteHistory={(row) => deleteHistory(editId, row)}
          saving={saving}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
